"use client"

import { useActionState } from "react"
import Input from "@modules/common/components/input"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { signup } from "@lib/data/customer"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
  next?: string
  email?: string
}

const Register = ({ setCurrentView, next, email }: Props) => {
  const [message, formAction] = useActionState(signup, null)

  return (
    <div
      className="max-w-sm flex flex-col items-center"
      data-testid="register-page"
    >
      <h1 className="text-large-semi uppercase mb-6">
        Crear cuenta
      </h1>
      <p className="text-center text-base-regular text-ui-fg-base mb-4">
        Crea tu cuenta para seguir tus pedidos, guardar direcciones y comprar más rápido.
      </p>
      {message?.state === "verification_required" && (
        <div
          className="w-full mb-4 text-center text-base-regular text-ui-fg-base bg-ui-bg-subtle border border-ui-border-base rounded-rounded p-4"
          data-testid="register-verification-message"
        >
          Te hemos enviado un enlace de verificación a <strong>{message.email}</strong>.
          Revisa tu bandeja de entrada para verificar tu email y después inicia sesión.
        </div>
      )}
      <form className="w-full flex flex-col" action={formAction}>
        {next && <input type="hidden" name="next" value={next} />}
        <div className="flex flex-col w-full gap-y-2">
          <Input
            label="Nombre"
            name="first_name"
            required
            autoComplete="given-name"
            data-testid="first-name-input"
          />
          <Input
            label="Apellidos"
            name="last_name"
            autoComplete="family-name"
            data-testid="last-name-input"
          />
          <Input
            label="Correo electrónico"
            name="email"
            required
            type="email"
            autoComplete="email"
            defaultValue={email}
            data-testid="email-input"
          />
          <Input
            label="Teléfono (opcional, para avisarte de la entrega)"
            name="phone"
            type="tel"
            autoComplete="tel"
            data-testid="phone-input"
          />
          <Input
            label="Contraseña"
            name="password"
            required
            type="password"
            autoComplete="new-password"
            minLength={8}
            aria-describedby="password-hint"
            data-testid="password-input"
          />
          <p id="password-hint" className="text-small-regular text-zl-steel">
            Mínimo 8 caracteres.
          </p>
        </div>
        <ErrorMessage
          error={message?.state === "error" ? message.error : null}
          data-testid="register-error"
        />
        <span className="text-center text-ui-fg-base text-small-regular mt-6">
          Al crear una cuenta, aceptas la{" "}
          <LocalizedClientLink href="/legal/privacidad" className="underline">
            política de privacidad
          </LocalizedClientLink>{" "}
          y las{" "}
          <LocalizedClientLink href="/legal/condiciones" className="underline">
            condiciones de uso
          </LocalizedClientLink>
          .
        </span>
        <SubmitButton className="w-full mt-6" data-testid="register-button">
          Crear cuenta
        </SubmitButton>
      </form>
      <span className="text-center text-ui-fg-base text-small-regular mt-6">
        ¿Ya tienes cuenta?{" "}
        <button
          onClick={() => setCurrentView(LOGIN_VIEW.SIGN_IN)}
          className="underline"
        >
          Iniciar sesión
        </button>
        .
      </span>
    </div>
  )
}

export default Register
